import React, { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { CheckCircle, Ticket, Mail, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const PaymentSuccess = ({ email, ticketCount, eventTitle, onContinue, onViewTickets }) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Trigger confetti explosion
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const colors = ['#f97316', '#3b82f6', '#22c55e', '#eab308', '#ec4899'];

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const runConfetti = () => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return;
      }

      const particleCount = 50 * (timeLeft / duration);

      // Confetti from both sides
      confetti({
        particleCount: Math.floor(particleCount / 2),
        startVelocity: 30,
        spread: 360,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: colors,
        ticks: 60,
        gravity: 1,
        scalar: randomInRange(0.8, 1.2),
        drift: randomInRange(-0.5, 0.5)
      });

      confetti({
        particleCount: Math.floor(particleCount / 2),
        startVelocity: 30,
        spread: 360,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: colors,
        ticks: 60,
        gravity: 1,
        scalar: randomInRange(0.8, 1.2),
        drift: randomInRange(-0.5, 0.5)
      });

      requestAnimationFrame(runConfetti);
    };

    // Initial big burst from center
    confetti({
      particleCount: 100,
      spread: 100,
      origin: { x: 0.5, y: 0.5 },
      colors: colors,
      startVelocity: 45,
      ticks: 100
    });

    runConfetti();

    // Show content after a brief delay
    setTimeout(() => setShowContent(true), 300);

    // Cleanup
    return () => {
      confetti.reset();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className={`max-w-md w-full mx-4 text-center transition-all duration-700 ${
        showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}>
        {/* Success Icon */}
        <div className="relative mb-6">
          <div className="w-24 h-24 mx-auto rounded-full bg-green-500/20 flex items-center justify-center animate-bounce-slow">
            <CheckCircle className="w-14 h-14 text-green-500" />
          </div>
          <div className="absolute inset-0 w-24 h-24 mx-auto rounded-full bg-green-500/10 animate-ping" />
        </div>

        {/* Success Message */}
        <h1 className="text-3xl font-bold mb-2 text-green-500">
          ¡Pago Exitoso!
        </h1>
        <p className="text-xl text-foreground mb-6">
          Tu compra ha sido confirmada
        </p>

        {/* Order Summary Card */}
        <div className="glass-card p-6 mb-6 text-left">
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Ticket className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="font-semibold">{ticketCount} Ticket{ticketCount > 1 ? 's' : ''}</p>
              <p className="text-sm text-muted-foreground">{eventTitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <Mail className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Enviado a</p>
              <p className="font-semibold">{email}</p>
            </div>
          </div>
        </div>

        {/* Info Text */}
        <p className="text-sm text-muted-foreground mb-6">
          Hemos enviado tus tickets con código QR a tu correo electrónico. 
          Revisa tu bandeja de entrada.
        </p>

        {/* Action Buttons */}
        <div className="space-y-3">
          {onViewTickets && (
            <Button 
              className="w-full btn-primary h-12"
              onClick={onViewTickets}
              data-testid="view-tickets-button"
            >
              <Ticket className="w-4 h-4 mr-2" />
              Ver mis Tickets
            </Button>
          )}
          <Button 
            variant="outline"
            className="w-full h-12"
            onClick={onContinue}
            data-testid="continue-button"
          >
            Seguir explorando eventos
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Custom animation styles */}
      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccess;
